import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import NotificationNoticeReader from "../components/notifications/NotificationNoticeReader";
import { notifyNotificationsUpdated } from "../hooks/useNotificationUnreadCount";
import {
  fetchNotifications,
  markNotificationRead,
  unwrapNotificationsPayload,
} from "../services/notificationService";

function channelLabel(channel) {
  return channel === "EMAIL" ? "Email" : "In-app";
}

export default function NotificationNoticePage() {
  const { noticeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [notice, setNotice] = useState(location.state?.notice ?? null);
  const [loading, setLoading] = useState(!location.state?.notice);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const resolveNotice = async () => {
      if (location.state?.notice?.id === noticeId) {
        setNotice(location.state.notice);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const { data } = await fetchNotifications();
        const { results } = unwrapNotificationsPayload(data);
        const found = results.find((n) => String(n.id) === String(noticeId));
        if (cancelled) return;
        if (!found) {
          setError("This notice could not be found.");
          setNotice(null);
        } else {
          setNotice(found);
        }
      } catch {
        if (!cancelled) {
          setError("Could not load this notice.");
          setNotice(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    resolveNotice();
    return () => {
      cancelled = true;
    };
  }, [noticeId, location.state?.notice]);

  useEffect(() => {
    if (!notice?.id || notice.is_read) return;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await markNotificationRead(notice.id);
        if (cancelled) return;
        setNotice((prev) => (prev ? { ...prev, ...data, is_read: true } : prev));
        notifyNotificationsUpdated();
      } catch {
        /* non-blocking */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [notice?.id, notice?.is_read]);

  const handleBack = () => {
    navigate("/notifications");
  };

  return (
    <div className="page investor-premium investor-panel inv-notifications-page inv-notice-detail-page">
      <p className="inv-notice-detail-page__crumb">
        <Link to="/notifications">← Notifications</Link>
      </p>

      {loading ? <p className="muted inv-notice-detail-page__loading">Loading notice…</p> : null}
      {error ? <p className="msg msg-error">{error}</p> : null}

      {!loading && !error && notice ? (
        <div className="inv-notice-detail-page__sheet">
          <NotificationNoticeReader notice={notice} channelLabel={channelLabel} onBack={handleBack} />
        </div>
      ) : null}

      {!loading && !error && !notice ? (
        <p className="muted">
          <Link to="/notifications">Return to notifications</Link>
        </p>
      ) : null}
    </div>
  );
}
